import { apiUrl, currentUser, pollInterval, rolePerms, set } from '../state';
import { esc } from './ui';
import { loadTickets } from './tickets';

const POPUP_SVG: Record<string, string> = {
  success: `<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="32" fill="#DCFCE7"/><circle cx="32" cy="32" r="22" fill="#16A34A"/><path d="M21 32l8 8 14-14" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  error:   `<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="32" fill="#FEE2E2"/><circle cx="32" cy="32" r="22" fill="#DC2626"/><path d="M24 24l16 16M40 24L24 40" stroke="#fff" stroke-width="3" stroke-linecap="round"/></svg>`,
  warning: `<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="32" fill="#FFEDD5"/><circle cx="32" cy="32" r="22" fill="#EA580C"/><path d="M32 22v13" stroke="#fff" stroke-width="3" stroke-linecap="round"/><circle cx="32" cy="41" r="1.5" fill="#fff" stroke="#fff" stroke-width="1"/></svg>`,
  logout:  `<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="32" fill="#FEF0E6"/><path d="M30 22H24a2 2 0 00-2 2v16a2 2 0 002 2h6" stroke="#E8640A" stroke-width="2.5" stroke-linecap="round"/><path d="M36 26l6 6-6 6M42 32H28" stroke="#E8640A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
};

interface PopupButton { label: string; cls: string; cb: (() => void) | null; }
let _apCallback: PopupButton[] | null = null;

export function showAuthPopup(iconKey: string, title: string, sub: string, buttons: PopupButton[]): void {
  const apIcon = document.getElementById('apIcon')!;
  apIcon.innerHTML = POPUP_SVG[iconKey] ?? POPUP_SVG.warning;
  document.getElementById('apTitle')!.textContent = title;
  document.getElementById('apSub')!.textContent = sub;
  const btnsEl = document.getElementById('apBtns')!;
  btnsEl.innerHTML = '';
  buttons.forEach((b, i) => {
    const btn = document.createElement('button');
    btn.className = `auth-popup-btn ${b.cls}`;
    btn.textContent = b.label;
    btn.onclick = () => _apBtnClick(i);
    btnsEl.appendChild(btn);
  });
  _apCallback = buttons;
  document.getElementById('authPopup')!.classList.add('show');
}

function _apBtnClick(i: number): void {
  document.getElementById('authPopup')!.classList.remove('show');
  const cb = _apCallback?.[i]?.cb;
  _apCallback = null;
  if (cb) cb();
}

export function togglePw(): void {
  const inp = document.getElementById('pwInp') as HTMLInputElement;
  const icon = document.getElementById('pwEyeIcon')!;
  inp.type = inp.type === 'password' ? 'text' : 'password';
  icon.className = inp.type === 'password' ? 'ti ti-eye' : 'ti ti-eye-off';
}

export function toggleFieldPw(inpId: string, eyeId: string): void {
  const inp = document.getElementById(inpId) as HTMLInputElement;
  const eye = document.getElementById(eyeId)!;
  inp.type = inp.type === 'password' ? 'text' : 'password';
  eye.className = inp.type === 'password' ? 'ti ti-eye' : 'ti ti-eye-off';
}

export async function doLogin(): Promise<void> {
  const username = (document.getElementById('usernameInp') as HTMLInputElement).value.trim();
  const password = (document.getElementById('pwInp') as HTMLInputElement).value;
  const errEl = document.getElementById('loginErr')!;
  if (!username || !password) {
    errEl.textContent = 'กรุณาใส่ username และ password';
    return;
  }
  const btn = document.getElementById('loginBtn') as HTMLButtonElement;
  btn.disabled = true;
  btn.innerHTML = '<i class="ti ti-loader spin"></i> กำลังตรวจสอบ...';
  errEl.textContent = '';
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    const res = await fetch(apiUrl, {
      method: 'POST',
      body: JSON.stringify({ action: 'login', username, password }),
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    });
    clearTimeout(timer);
    const data = await res.json();
    if (data.ok) {
      set.currentUser({ token: data.token, role: data.role, displayName: data.displayName, username: data.username });
      sessionStorage.setItem('hr_session', JSON.stringify(currentUser));
      showAuthPopup('success', 'เข้าสู่ระบบสำเร็จ', `ยินดีต้อนรับ, ${data.displayName}`, [
        { label: 'เริ่มใช้งาน', cls: 'ok', cb: () => showApp() },
      ]);
    } else {
      errEl.textContent = data.error ?? 'เข้าสู่ระบบไม่สำเร็จ';
    }
  } catch (e) {
    const isTimeout = (e as Error).name === 'AbortError';
    errEl.textContent = isTimeout
      ? 'หมดเวลาเชื่อมต่อ — Worker ไม่ตอบสนอง'
      : `เชื่อมต่อ Worker ไม่ได้: ${(e as Error).message}`;
  }
  btn.disabled = false;
  btn.innerHTML = '<i class="ti ti-login"></i> เข้าสู่ระบบ';
}

export function isAdmin(): boolean {
  return currentUser?.role === 'admin';
}

export function showApp(): void {
  document.getElementById('loginOverlay')!.style.display = 'none';
  document.getElementById('mainApp')!.style.display = 'grid';
  if (!currentUser) return;
  const roleBadge = document.getElementById('hdrRoleBadge')!;
  roleBadge.textContent = currentUser.role.toUpperCase();
  roleBadge.className = `hdr-role-badge role-${esc(currentUser.role)}`;
  document.getElementById('hdrName')!.textContent = currentUser.displayName;
  document.querySelectorAll('.admin-only').forEach(el => el.classList.toggle('hidden', !isAdmin()));
  applyRolePerms();
  loadTickets();
  if (pollInterval) clearInterval(pollInterval);
  set.pollInterval(setInterval(() => loadTickets(), 15000));
}

export function applyRolePerms(): void {
  if (!currentUser) return;
  const perms = rolePerms[currentUser.role] ?? rolePerms.hr;
  const sumTab = document.querySelector('[data-page="summary"]');
  if (sumTab) sumTab.classList.toggle('hidden', !perms.summary);
  document.querySelectorAll('.admin-only').forEach(el => el.classList.toggle('hidden', !isAdmin()));
}

export function logout(): void {
  showAuthPopup('logout', 'ออกจากระบบ', 'คุณต้องการออกจากระบบใช่หรือไม่?', [
    { label: 'ยกเลิก', cls: 'neutral', cb: null },
    { label: 'ออกจากระบบ', cls: 'danger', cb: () => {
      sessionStorage.removeItem('hr_session');
      set.currentUser(null);
      if (pollInterval) clearInterval(pollInterval);
      location.reload();
    }},
  ]);
}
